export class BundleCartError extends Error {
  constructor(code) {
    super(code);
    this.name = 'BundleCartError';
    this.code = code;
  }
}

const id = value => String(value ?? '');
const plan = item => id(item.selling_plan_allocation?.selling_plan?.id);
const positiveInteger = value => Number.isSafeInteger(value) && value > 0;
const totalQuantity = items => items.reduce((sum, item) => sum + item.quantity, 0);

export const FIRST_ORDER_EXTRAS = Object.freeze([
  { id: 50765178241282, handle: '3-cash-back' },
  { id: 50765177815298, handle: 'free-shipping' },
  { id: 50765176635650, handle: 'oura-ring-5-prize-draw-entry' },
  { id: 50765178568962, handle: 'the-complete-gut-relief-playbook' },
  { id: "50805479538946", handle: "gut-health-1" }
]);
const extraMarker = 'kindgut-pdp-first-order-v1';
const isOfferExtra = item => FIRST_ORDER_EXTRAS.some(extra => id(extra.id) === id(item.variant_id)) && item.properties?._kindgut_offer === extraMarker;
const variantSignature = item => JSON.stringify([id(item.variant_id), plan(item), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))]);

const isLegacyCore = item => ['50754325741826', '50754326298882'].includes(id(item.variant_id)) && plan(item) === '7274397954';
const isLegacyGift = item => id(item.variant_id) === '50754326233346' && item.properties?._kindgut_offer === extraMarker;
const isParent = item => id(item.variant_id) === '50805360034050';

const validConfiguration = config => config.ready === true && id(config.sellingPlanId) === '7287341314' && Array.isArray(config.coreVariantIds) && config.coreVariantIds.length === 1 && id(config.coreVariantIds[0]) === '50805360034050';

export function inspectBundleCart(cart, config) {
  if (!cart || !Array.isArray(cart.items)) throw new BundleCartError('verification');
  const groups = config.coreVariantIds.map(variant => cart.items.filter(item => id(item.variant_id) === id(variant) && plan(item) === id(config.sellingPlanId)));
  const included = new Set(groups.flat().map(item => item.key));
  const other = cart.items.filter(item => !included.has(item.key));
  const quantities = groups.map(totalQuantity);
  const issues = [];
  if (!validConfiguration(config)) issues.push('unavailable');
  if (cart.items.some(item => !positiveInteger(item.quantity) || !item.key)) issues.push('verification');
  if (!cart.items.length) issues.push('empty');
  const extras = cart.items.filter(isOfferExtra);
  const legacy = cart.items.filter(item => isLegacyCore(item) || isLegacyGift(item));
  const hasBundle = quantities.some(Boolean) || extras.length > 0 || legacy.length > 0 || cart.items.some(isParent);
  if (legacy.length) issues.push('legacy_cart');
  if (cart.items.some(item => isParent(item) && plan(item) !== id(config.sellingPlanId))) issues.push('plan');
  if (hasBundle && !quantities.every(Boolean)) issues.push('incomplete');
  if (hasBundle && (quantities[0] !== 1 || groups[0].length !== 1)) issues.push('quantity');
  if (hasBundle && cart.currency !== 'USD') issues.push('currency');
  if (groups.flat().some(item => item.final_line_price !== 5498 * item.quantity)) issues.push('price');
  if (hasBundle && FIRST_ORDER_EXTRAS.some(extra => totalQuantity(extras.filter(item => id(item.variant_id) === id(extra.id))) !== 1)) issues.push('incomplete');
  if (extras.some(item => plan(item))) issues.push('extra_recurring');
  if (extras.some(item => item.final_line_price !== 0)) issues.push('gift_price');
  return { valid: issues.length === 0, issues, groups, gifts: extras, other, quantities, quantity: quantities[0] || 0 };
}

export function validateBundleItems(items, config) {
  if (!validConfiguration(config)) throw new BundleCartError('unavailable');
  if (!Array.isArray(items) || items.length !== 6) throw new BundleCartError('invalid_items');
  const cores = config.coreVariantIds.map(variant => items.filter(item => id(item.id) === id(variant)));
  if (cores.some(rows => rows.length !== 1 || rows[0].quantity !== 1)) throw new BundleCartError('invalid_items');
  if (cores.flat().some(item => id(item.selling_plan) !== id(config.sellingPlanId))) throw new BundleCartError('plan');
  for (const extra of FIRST_ORDER_EXTRAS) {
    const rows = items.filter(item => id(item.id) === id(extra.id));
    if (rows.length !== 1 || rows[0].quantity !== 1 || rows[0].selling_plan != null || rows[0].properties?._kindgut_offer !== extraMarker) throw new BundleCartError('invalid_items');
  }
  return items;
}

export function createBundleCartEngine(config, api, hooks = {}) {
  let queue = Promise.resolve();
  let pending = 0;
  let revision = 0;
  let cart = null;
  let error = null;
  const state = () => ({ cart, busy: pending > 0, revision, error, inspection: cart ? inspectBundleCart(cart, config) : null });
  const emit = () => hooks.state?.(state());
  const fresh = async () => {
    cart = await api.read();
    inspectBundleCart(cart, config);
    return cart;
  };
  const run = work => {
    revision += 1;
    pending += 1;
    error = null;
    emit();
    const result = queue.then(() => { error = null; emit(); return work(); }).catch(cause => {
      error = cause instanceof BundleCartError ? cause : new BundleCartError('network');
      if (error.message === error.code) error.message = config.messages?.[error.code] || ({ legacy_cart: 'Please select the current Gut Health Bundle offer to update your cart before checkout.', currency: 'This offer is available in USD. Please contact KindGut support.', gift_price: 'A first-order extra is not free in your cart. Please contact KindGut support before checking out.', extra_recurring: 'A first-order extra incorrectly has a recurring plan. Please contact KindGut support.' }[error.code]) || error.message;
      throw error;
    }).finally(() => {
      pending -= 1;
      emit();
    });
    queue = result.catch(() => {});
    return result;
  };
  const assertValid = () => {
    const current = inspectBundleCart(cart, config);
    if (!current.valid) throw new BundleCartError(current.issues[0]);
    return current;
  };
  const removeKeys = async keys => {
    if (!keys.length) return;
    const updates = Object.fromEntries(keys.map(key => [key, 0]));
    try { await api.update(updates); } catch (cause) {
      await fresh();
      if (cart.items.some(item => keys.includes(item.key))) throw cause;
    }
    await fresh();
  };
  const setCoreTotal = async (variant, quantity) => {
    await fresh();
    const maxChanges = cart.items.length + 2;
    for (let attempt = 0; attempt < maxChanges; attempt += 1) {
      const rows = cart.items.filter(item => id(item.variant_id) === id(variant) && plan(item) === id(config.sellingPlanId));
      const currentQuantity = totalQuantity(rows);
      if (currentQuantity === quantity) return;
      if (!rows.length) throw new BundleCartError('incomplete');
      const difference = quantity - currentQuantity;
      const row = difference > 0 ? rows[0] : rows[rows.length - 1];
      const nextQuantity = Math.max(0, row.quantity + difference);
      await api.change({ line: cart.items.indexOf(row) + 1, quantity: nextQuantity });
      await fresh();
    }
    throw new BundleCartError('verification');
  };
  const refresh = () => run(async () => {
    await fresh();
    await hooks.changed?.(cart, 'update');
    return cart;
  });
  const prepareForCheckout = (verify = () => {}, proceed = () => {}) => run(async () => {
    verify();
    await fresh();
    verify();
    verify();
    assertValid();
    // Keep the final check and navigation synchronous within this queued operation.
    proceed();
    return cart;
  });
  const removeBundle = () => run(async () => {
    await fresh();
    const inspection = inspectBundleCart(cart, config);
    const keys = cart.items.filter(item => isParent(item) || isLegacyCore(item) || isLegacyGift(item) || isOfferExtra(item)).map(item => item.key);
    await removeKeys(keys);
    if (cart.items.some(item => keys.includes(item.key))) throw new BundleCartError('verification');
    await hooks.changed?.(cart, 'remove');
    return cart;
  });
  const selectBundle = async (items, verify = () => {}) => {
    validateBundleItems(items, config);
    verify();
    // Independent reads run together; settle both before releasing the operation queue.
    const checks = await Promise.allSettled([Promise.resolve().then(() => api.checkExtras?.()), fresh()]);
    const failed = checks.find(result => result.status === 'rejected');
    if (failed) throw failed.reason;
    verify();
    const before = structuredClone(cart);
    const matches = (line, item) => id(line.variant_id) === id(item.id) && plan(line) === id(item.selling_plan) && (!item.properties?._kindgut_offer || line.properties?._kindgut_offer === item.properties._kindgut_offer);
    const quantity = (value, item) => value.items.filter(line => matches(line, item)).reduce((sum, line) => sum + line.quantity, 0);
    // An offer button selects one complete bundle, including one set of first-order extras.
    // Repeated clicks must reuse that selection rather than incrementing paid gift quantities.
    const target = item => isParent(item) || isLegacyCore(item) || isLegacyGift(item) || isOfferExtra(item);
    const alreadySelected = !cart.items.some(item => isLegacyCore(item) || isLegacyGift(item)) && cart.items.filter(target).length === items.length && items.every(item => quantity(cart, item) === item.quantity);
    if (!alreadySelected) {
      const selectedKeys = cart.items.filter(target).map(line => line.key);
      await removeKeys(selectedKeys);
      verify();
      await api.add(items);
      await fresh();
      verify();
    }
    if (items.some(item => quantity(cart, item) !== item.quantity)) throw new BundleCartError('add_uncertain');
    const quantitiesBySignature = value => {
      const result = new Map();
      for (const line of value.items.filter(item => !target(item))) {
        const signature = variantSignature(line);
        result.set(signature, (result.get(signature) || 0) + line.quantity);
      }
      return result;
    };
    const prior = quantitiesBySignature(before), current = quantitiesBySignature(cart);
    if (prior.size !== current.size || [...prior].some(([signature, amount]) => current.get(signature) !== amount)) throw new BundleCartError('verification');
    assertValid();
    await hooks.changed?.(cart, 'add');
    verify();
    return cart;
  };
  const addBundle = items => run(() => selectBundle(items));
  const addBundleAndCheckout = (items, verify = () => {}, proceed = () => {}) => run(async () => {
    await selectBundle(items, verify);
    // No second read: this operation owns the queue and has just verified the fresh cart.
    // The caller checks external mutation epoch and queued-operation revision synchronously.
    verify();
    assertValid();
    proceed();
    return cart;
  });
  const setQuantity = quantity => {
    if (quantity === 0) return removeBundle();
    return run(async () => {
      if (quantity !== 1) throw new BundleCartError('quantity');
      await fresh();
      const before = inspectBundleCart(cart, config);
      const blocking = before.issues.filter(issue => issue !== 'quantity');
      if (blocking.length) throw new BundleCartError(blocking[0]);
      try {
        for (const variant of config.coreVariantIds) await setCoreTotal(variant, quantity);
        assertValid();
        if (inspectBundleCart(cart, config).quantity !== quantity) throw new BundleCartError('quantity');
      } catch (cause) {
        await fresh();
        const after = inspectBundleCart(cart, config);
        if (!(after.valid && after.quantity === quantity)) {
          const safeQuantity = Math.min(...before.quantities, ...after.quantities);
          if (positiveInteger(safeQuantity) && after.quantities.every(Boolean)) {
            try {
              for (const variant of config.coreVariantIds) await setCoreTotal(variant, safeQuantity);
            } catch { /* The next fresh check must keep checkout blocked if repair was incomplete. */ }
          }
          await fresh();
          await hooks.changed?.(cart, 'update');
          throw cause;
        }
      }
      await hooks.changed?.(cart, 'update');
      return cart;
    });
  };
  return Object.freeze({ refresh, prepareForCheckout, addBundle, addBundleAndCheckout, setQuantity, removeBundle, getState: state });
}

export function createAjaxCartApi(config, fetcher = globalThis.fetch.bind(globalThis)) {
  const root = config.cartUrl.replace(/\/$/, '');
  const request = async (path, body) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetcher(`${root}${path}`, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const result = await response.json();
      if (!response.ok || result.errors || result.status >= 400) { const error = new BundleCartError('network'); error.message = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'Shopify could not update your cart.'; throw error; }
      return result;
    } finally { clearTimeout(timer); }
  };
  return {
    checkExtras: async () => {
      const store = config.cartUrl.replace(/cart\/?$/, '');
      await Promise.all([...FIRST_ORDER_EXTRAS, { id: 50805360034050, handle: 'the-gut-health-bundle' }].map(async extra => {
        const response = await fetcher(`${store}products/${extra.handle}.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) { const error = new BundleCartError('extra_unavailable'); error.message = 'A first-order extra is not published for this storefront. Please contact KindGut support.'; throw error; }
        const product = await response.json();
        const variant = product.variants?.find(value => id(value.id) === id(extra.id));
        if (extra.id === 50805360034050 && !variant?.selling_plan_allocations?.some(allocation => id(allocation.selling_plan_id) === id(config.sellingPlanId))) throw new BundleCartError('plan');
        if (!variant?.available) { const error = new BundleCartError('extra_unavailable'); error.message = `${product.title || 'A first-order extra'} is currently unavailable for checkout. Please contact KindGut support.`; throw error; }
      }));
    },
    read: () => request('.js'),
    add: items => request('/add.js', { items }),
    change: body => request('/change.js', body),
    update: updates => request('/update.js', { updates }),
  };
}

export function installBundleCart(config, environment = window) {
  const document = environment.document;
  let lastState = { busy: true, error: null, inspection: null };
  let eventsModule;
  let externalPending = 0;
  let mutationEpoch = 0;
  let observingCart = false;
  let navigating = false;
  const checkoutSelector = '[data-kindgut-checkout],[data-pc-cart-checkout],button[name="checkout"],input[name="checkout"]';
  const messages = config.messages || {};
  const paint = () => {
    const { error, inspection } = lastState;
    const busy = lastState.busy || navigating || !observingCart;
    const blocked = busy || externalPending > 0 || error || !inspection?.valid;
    const code = busy ? 'checking' : error?.code || inspection?.issues[0] || '';
    const message = messages[code] || error?.message || (code ? messages.verification : '');
    document.querySelectorAll(checkoutSelector).forEach(button => {
      if ('disabled' in button) button.disabled = Boolean(blocked);
      button.setAttribute('aria-disabled', String(Boolean(blocked)));
      button.setAttribute('aria-busy', String(busy));
    });
    const purchases = '[data-kindgut-purchase],[data-pc-bp] [data-cta],[data-pc-bp] [data-leadtag="checkout"],[data-pc-bp] .hbo-stk__btn,[data-pc-bp] [data-tier],[data-hyb-bbm] [data-bbf-atc],[data-hyb-bbm] [data-bbf-stickybtn]';
    document.querySelectorAll(purchases).forEach(button => {
      const disabled = !validConfiguration(config) || busy || externalPending > 0 || button.closest('[data-pc-bp]')?.dataset.kindgutPurchaseBlocked === 'true';
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
      if (!validConfiguration(config) && !button.parentElement.querySelector('[data-kindgut-purchase-status]')) {
        const note = document.createElement('p');
        note.dataset.kindgutPurchaseStatus = '';
        note.setAttribute('role', 'status');
        note.style.cssText = 'font-size:13px;line-height:1.45;opacity:.75;margin:8px 0';
        note.textContent = messages.unavailable;
        button.after(note);
      }
    });
    if (validConfiguration(config)) document.querySelectorAll('[data-kindgut-purchase-status]').forEach(node => node.remove());
    document.querySelectorAll('[data-kindgut-cart-status]').forEach(node => {
      if (node.textContent !== message) node.textContent = message;
      node.hidden = !message;
    });
    document.querySelectorAll('[data-kindgut-remove-bundle]').forEach(button => {
      button.disabled = busy;
      button.hidden = !inspection || !inspection.groups.flat().length && !inspection.gifts.length;
    });
    if (error && lastState.cart) {
      document.querySelectorAll('[data-kindgut-variant-id][data-key]').forEach(row => {
        const item = lastState.cart.items.find(item => item.key === row.dataset.key);
        const input = row.querySelector('input[name="updates[]"]');
        if (item && input) input.value = String(item.quantity);
      });
    }
    document.querySelectorAll('[data-kindgut-recheck]').forEach(button => {
      button.disabled = busy;
      button.hidden = !error;
    });
  };
  const engine = createBundleCartEngine(config, createAjaxCartApi(config, environment.fetch.bind(environment)), {
    state(value) { lastState = value; paint(); },
    async changed(cart, action) {
      environment.dispatchEvent(new CustomEvent('kindgut:cart-updated', { detail: { cart, action } }));
      try {
        eventsModule ||= import('@shopify/events');
        const { CartLinesUpdateEvent } = await eventsModule;
        const event = new CartLinesUpdateEvent({
          action: action === 'add' ? 'update' : action, context: 'cart', lines: cart.items.map(item => ({ id: item.key, quantity: item.quantity })),
          promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { source: 'kindgut-bundle-cart', items: cart.items, itemCount: cart.item_count, didError: false } }),
        });
        event.kindgutBundleGuard = true;
        document.dispatchEvent(event);
      } catch { environment.dispatchEvent(new CustomEvent('kindgut:cart-display-stale')); }
    },
  });
  const swallow = promise => promise.catch(() => {});
  const stop = event => { event.preventDefault(); event.stopImmediatePropagation(); };
  const checkout = async (destination = config.checkoutUrl, items = null) => {
    if (!observingCart || navigating || lastState.busy || externalPending) {
      const error = new BundleCartError('checking');
      error.message = messages.checking || messages.verification || error.message;
      throw error;
    }
    navigating = true;
    const epoch = mutationEpoch;
    let revision;
    const verify = () => {
      if (!observingCart || externalPending || mutationEpoch !== epoch || engine.getState().revision !== revision) {
        throw new BundleCartError('verification');
      }
    };
    try {
      const proceed = () => {
        verify();
        environment.location.assign(destination);
      };
      const preparation = items
        ? engine.addBundleAndCheckout(items, verify, proceed)
        : engine.prepareForCheckout(verify, proceed);
      revision = engine.getState().revision;
      await preparation;
    } catch (error) {
      navigating = false;
      paint();
      throw error;
    }
  };
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target) return;
    const rowRemove = target.closest('.cart-items__remove');
    const row = rowRemove?.closest('[data-kindgut-variant-id][data-key]');
    const rowItem = row && lastState.cart?.items.find(item => item.key === row.dataset.key);
    const removesBundleRow = rowItem && config.coreVariantIds.some(variant => id(variant) === id(rowItem.variant_id)) && plan(rowItem) === id(config.sellingPlanId);
    const remove = target.closest('[data-kindgut-remove-bundle]') || removesBundleRow;
    if (remove) {
      stop(event);
      if (!lastState.busy) swallow(engine.removeBundle());
      return;
    }
    if (target.closest('[data-kindgut-recheck]')) { stop(event); swallow(engine.refresh()); return; }
    const control = target.closest(`${checkoutSelector},a[href]`);
    if (!control) return;
    const path = control.tagName === 'A' ? new URL(control.href, environment.location.href).pathname : '';
    if (control.matches(checkoutSelector) || /\/(?:checkout|checkouts)(?:\/|$)/.test(path)) { stop(event); swallow(checkout(control.dataset.kindgutCheckoutUrl || config.checkoutUrl)); }
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = new URL(form.action, environment.location.href).pathname;
    if (/\/(?:checkout|checkouts)(?:\/|$)/.test(action) || event.submitter?.matches(checkoutSelector)) { stop(event); swallow(checkout()); }
    else if (form.matches('.cart-form,[data-kindgut-cart-form]')) { stop(event); swallow(engine.refresh()); }
  }, true);
  const observer = new MutationObserver(paint);
  observer.observe(document.body, { childList: true, subtree: true });
  environment.addEventListener('pageshow', event => { if (event.persisted) { navigating = false; swallow(engine.refresh()); } });
  environment.addEventListener('focus', () => swallow(engine.refresh()));
  eventsModule ||= import('@shopify/events');
  eventsModule.then(({ StandardEvents }) => {
    if (!StandardEvents?.cartLinesUpdate) throw new BundleCartError('verification');
    const trackMutation = event => {
      if (event.kindgutBundleGuard) return;
      mutationEpoch += 1;
      externalPending += 1;
      paint();
      Promise.resolve(event.promise).catch(() => {}).finally(() => {
        externalPending -= 1;
        swallow(engine.refresh());
      });
    };
    for (const type of new Set([StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate, StandardEvents.cartNoteUpdate].filter(Boolean))) {
      document.addEventListener(type, trackMutation, true);
    }
    observingCart = true;
    paint();
  }).catch(() => { /* Checkout stays blocked if cart mutations cannot be observed. */ });
  const publicApi = Object.freeze({ ...engine, checkout, addBundleAndCheckout: (items, destination) => checkout(destination, items), refreshUI: paint, isReady: () => Boolean(validConfiguration(config) && observingCart && !navigating && !lastState.busy && !externalPending) });
  environment.KindGutBundleCart = publicApi;
  swallow(engine.refresh());
  return publicApi;
}

if (typeof window !== 'undefined' && !window.KindGutBundleCart) {
  const element = document.getElementById('kindgut-bundle-cart-config');
  if (element) {
    try { installBundleCart(JSON.parse(element.textContent)); } catch { /* Server-rendered checkout remains disabled. */ }
  }
}


